
import { Button, Checkbox, Input, Textarea } from '@/components/ui';
import { PrayerFormStepNav } from './PrayerFormStepNav';
import type { PrayerFormData, ValidationErrors } from '@/lib/validation';
import { cn } from '@/lib/utils';

interface PrayerFormStepShareProps {
  formData: PrayerFormData;
  errors: ValidationErrors;
  onFieldChange: <K extends keyof PrayerFormData>(field: K, value: PrayerFormData[K]) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function PrayerFormStepShare({
  formData,
  errors,
  onFieldChange,
  onBack,
  onContinue,
}: PrayerFormStepShareProps) {
  const isPraise = formData.type === 'praise_report';

  return (
    <div className="space-y-10">
      <Textarea
        id="prayer-body"
        variant="borderless"
        label={isPraise ? 'Your answered prayer' : 'Your prayer request'}
        placeholder={
          isPraise
            ? 'Share what God has done in your life...'
            : 'Share your prayer request with us...'
        }
        value={formData.body}
        onChange={(e) => onFieldChange('body', e.target.value)}
        error={errors.body}
        showCharCount
        maxLength={250}
        required
      />

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First name (optional)"
            placeholder="Jane"
            variant="minimal"
            value={formData.firstName}
            onChange={(e) => onFieldChange('firstName', e.target.value)}
            error={errors.firstName}
            autoComplete="given-name"
          />
          <Input
            label="Last name (optional)"
            placeholder="Smith"
            variant="minimal"
            value={formData.lastName}
            onChange={(e) => onFieldChange('lastName', e.target.value)}
            error={errors.lastName}
            autoComplete="family-name"
          />
        </div>
        <div
          className={cn(
            'transition-opacity duration-200',
            formData.is_anonymous && 'opacity-50'
          )}
        >
          <Input
            id="prayer-location"
            label="Location (optional)"
            placeholder="City, State"
            variant="minimal"
            value={formData.location}
            onChange={(e) => onFieldChange('location', e.target.value)}
            error={errors.location}
            autoComplete="address-level2"
            disabled={formData.is_anonymous}
          />
        </div>
        <Checkbox
          id="prayer-anonymous"
          label="Submit anonymously"
          checked={formData.is_anonymous}
          onChange={(e) => onFieldChange('is_anonymous', e.target.checked)}
        />
      </div>

      <PrayerFormStepNav onPrevious={onBack}>
        <Button type="button" variant="primary" size="lg" fullWidth onClick={onContinue}>
          Next →
        </Button>
      </PrayerFormStepNav>
    </div>
  );
}
